// src/contexts/AuthProvider.jsx
// ======================================================
// 🔐 AUTH PROVIDER - V17 ENTERPRISE (BLINDADO + TRIAL CHECK + MULTI-DEVICE)
// ======================================================
// 🔥 V17: login/signUp blindados con timeout + rate limit + network check
// 🔥 V16: Verificación de trial al login — redirige a /planos si expiró
// ✅ Autenticação UNIFICADA (Firebase + DEMO)
// ✅ Compatível 100% com DataContext
// ✅ Persistência de sessão inteligente
// ✅ Multi-dispositivo (modo Firebase) - V15: CARGA DESDE FIRESTORE
// ✅ Offline-first (modo DEMO)
// ✅ Notificações profissionais
// ✅ White-label ready
// ✅ Zero breaking changes
// ✅ Login com Google integrado
// 🔥 V15: MULTI-DEVICE - Carga desde Firestore si localStorage está vacío
// 🔥 V15: getUserFromFirestore() - Fuente de verdad en la nube
// 🔥 V15: Sincronización automática Firestore → localStorage
// 🔥 V14: Blindagem ANTI-ZOMBIE - Documentos eliminados NUNCA resucitan
// 🔥 V14: _deletedCache persistente en localStorage
// 🔥 V14: onSnapshot filtra documentos marcados como eliminados
// 🔥 V14: lerFinanceiroLocal filtra eliminados del cache
// 🔥 V14: Métodos deleteProperty/Client/Task/Financeiro/Document marcan cache
// 🔥 V13: Blindagem completa anti-duplicados (3 camadas)
// 🔥 V13: Removido createFinanceiroFirestore e deleteFinanceiroFirestore
// 🔥 V13: createFinanceiro usa apenas CloudStrategy (idempotente)
// 🔥 V13: onFinanceiroSnapshot mantido com fallback localStorage
// 🔥 V12: Avatar sincroniza com Firebase Auth (photoURL) → multi-device
// 🔥 V11: onFinanceiroSnapshot fallback lê localStorage direto
// 🔥 V10: _cachedFinanceiro sincronizado com create/delete
// ======================================================

import React, { createContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import ConfirmModal from "../components/Modals/ConfirmModal";
import DataContext from "../lib/data/context/DataContext";
import authService from "../services/authService";
import { getUsersDatabase, saveUsersDatabase } from "../services/database";
import bcrypt from 'bcryptjs';

export const AuthContext = createContext();

// ======================================================
// 🔥 CACHE GLOBAL DE COMPANY (Evita leituras duplicadas)
// ======================================================
const companyCache = new Map();
const companyFetchInProgress = new Map();

// ======================================================
// 🔥 V17: CONFIG DE BLINDAJE
// ======================================================
const BLINDAJE_CONFIG = {
  TIMEOUT_MS: 15000,
  RATE_LIMIT_MAX: 5,
  RATE_LIMIT_WINDOW: 60000,
  COOLDOWN_AFTER_LIMIT: 30000,
};

// ======================================================
// 🔥 V17: RATE LIMIT GLOBAL
// ======================================================
const rateLimitTimestamps = [];
const checkRateLimit = () => {
  const now = Date.now();
  while (rateLimitTimestamps.length && now - rateLimitTimestamps[0] > BLINDAJE_CONFIG.RATE_LIMIT_WINDOW) {
    rateLimitTimestamps.shift();
  }
  if (rateLimitTimestamps.length >= BLINDAJE_CONFIG.RATE_LIMIT_MAX) {
    throw new Error('Muitas tentativas. Aguarde 30 segundos.');
  }
  rateLimitTimestamps.push(now);
};

const isOnline = () => typeof navigator !== 'undefined' ? navigator.onLine : true;

const timeoutPromise = (ms, label) => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`${label} excedeu ${ms}ms`)), ms);
  });
};

const executeWithTimeout = async (operation, label = 'auth') => {
  return Promise.race([
    operation(),
    timeoutPromise(BLINDAJE_CONFIG.TIMEOUT_MS, label),
  ]);
};

// ======================================================
// 🔥 V14: CACHE PERSISTENTE DE DOCUMENTOS ELIMINADOS
// ======================================================
const DELETED_CACHE_KEY = 'imoveispro_deleted_cache';

const getDeletedCache = () => {
  try {
    const cache = localStorage.getItem(DELETED_CACHE_KEY);
    return cache ? JSON.parse(cache) : [];
  } catch {
    return [];
  }
};

const addToDeletedCache = (id) => {
  try {
    const cache = getDeletedCache();
    if (!cache.includes(id)) {
      cache.push(id);
      if (cache.length > 500) {
        cache.splice(0, cache.length - 500);
      }
      localStorage.setItem(DELETED_CACHE_KEY, JSON.stringify(cache));
    }
  } catch (_) {}
};

const isDeleted = (id) => {
  try {
    return getDeletedCache().includes(id);
  } catch {
    return false;
  }
};

export function AuthProvider({ children }) {
  // ======================================================
  // 📊 ESTADO PRINCIPAL
  // ======================================================
  const [user, setUser] = useState(null);
  const [database, setDatabase] = useState(null);
  const [company, setCompany] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [modalData, setModalData] = useState(null);
  const [authMode, setAuthMode] = useState('demo');

  // ======================================================
  // 🔥 REFS PARA CONTROLE DE MONTAGEM E INICIALIZAÇÃO
  // ======================================================
  const mountedRef = useRef(true);
  const initializedRef = useRef(false);
  const dataContextRef = useRef(null);
  const userDataRef = useRef(null);
  const isSubmittingRef = useRef(false);

  // ======================================================
  // 📱 DETECÇÃO DE DISPOSITIVO
  // ======================================================
  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

  // ======================================================
  // 🔥 V16: VERIFICAR TRIAL AL INICIAR SESIÓN
  // ======================================================
  const checkTrialAndRedirect = useCallback(() => {
    try {
      const trialData = JSON.parse(localStorage.getItem('propzyn_trial') || 'null');
      if (trialData) {
        const now = new Date();
        const trialEnd = new Date(trialData.trialEnd);
        if (now > trialEnd) {
          console.log('⏰ [V17] Trial expirado, redirigiendo a /planos');
          if (typeof window !== 'undefined') {
            window.location.href = '/planos?trial=expired';
          }
          return true;
        }
        console.log(`⏳ [V17] Trial activo · ${trialData.trialPlan} · ${Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24))} días restantes`);
      }
    } catch {}
    return false;
  }, []);

  // ======================================================
  // 🔔 SISTEMA DE NOTIFICAÇÕES (MANTIDO 100%)
  // ======================================================
  const emitNotification = useCallback((type, message, options = {}) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:notification', {
        detail: {
          type,
          message,
          title: options.title,
          duration: options.duration || 5000,
          metadata: options.metadata,
          position: isMobile ? 'top-center' : options.position || 'top-right'
        }
      }));
    }
  }, [isMobile]);

  const emitAddNotification = useCallback((notification) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:addNotification', {
        detail: notification
      }));
    }
  }, []);

  // ======================================================
  // 🔥 V15 ENTERPRISE: CARGA DESDE FIRESTORE (MULTI-DEVICE)
  // ======================================================
  const getUserFromFirestore = useCallback(async (uid) => {
    try {
      const { db, firebaseAvailable } = await import('../lib/firebase/config');
      if (!firebaseAvailable || !db || db._isDummy) {
        console.log('📴 Firebase no disponible para carga multi-dispositivo');
        return null;
      }

      const { getFirestore, doc, getDoc } = await import('firebase/firestore');
      const firestoreDb = getFirestore();
      const userDocRef = doc(firestoreDb, 'users', uid);
      const userSnap = await getDoc(userDocRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        console.log(`✅ [V17] Datos de usuario cargados desde Firestore para: ${uid}`);
        localStorage.setItem('auth_user', JSON.stringify(userData));
        return userData;
      }
      
      console.log(`ℹ️ [V17] No se encontraron datos en Firestore para: ${uid}`);
      return null;
    } catch (error) {
      console.warn('⚠️ [V17] Error al cargar desde Firestore:', error.message);
      return null;
    }
  }, []);

  // ======================================================
  // 🛠️ FUNÇÃO AUXILIAR: Criar objeto user completo
  // ======================================================
  const createCompleteUserObject = useCallback((userData) => {
    if (!userData || (!userData.id && !userData.uid)) {
      console.error("❌ Dados de usuário inválidos para criar objeto completo");
      return null;
    }

    const userId = userData.uid || userData.id;
    const isAdmin = userData.role === "admin";
    const isCorretor = userData.role === "corretor";
    
    return {
      id: userId,
      uid: userId,
      email: userData.email,
      name: userData.name || userData.email?.split('@')[0] || 'Usuário',
      role: userData.role || 'usuario',
      company: userData.company || userData.companyId || `company_${userId}`,
      companyId: userData.companyId || userData.company || `company_${userId}`,
      avatar: userData.avatar || userData.photoURL || null,
      phone: userData.phone || userData.phoneNumber || '',
      color: userData.color || "#2563eb",
      createdAt: userData.createdAt || new Date().toISOString(),
      updatedAt: userData.updatedAt || new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      emailVerified: userData.emailVerified || false,
      canWrite: userData.canWrite !== false,
      canDelete: isAdmin || userData.canDelete === true,
      canRead: userData.canRead !== false,
      canManageUsers: isAdmin || userData.canManageUsers === true,
      canManageCompany: isAdmin || isCorretor || userData.canManageCompany === true,
      _isAuthenticated: true,
      _securityLevel: isAdmin ? "admin" : isCorretor ? "corretor" : "user",
      _tenantId: userData.company || userData.companyId || `company_${userId}`,
      _authProvider: userData._authProvider || 'demo',
      _permissions: userData._permissions || {
        create: true, read: true, update: true,
        delete: isAdmin, manageUsers: isAdmin,
        manageCompany: isAdmin || isCorretor
      }
    };
  }, []);

  // ======================================================
  // 🔥 V14: Helper para ler financeiro do localStorage (FILTRA ELIMINADOS)
  // ======================================================
  const lerFinanceiroLocal = useCallback(() => {
    try {
      const uid = userDataRef.current?.uid || userDataRef.current?.id;
      if (!uid) return [];
      const dbKey = `imoveispro_db_user_${uid}`;
      const db = JSON.parse(localStorage.getItem(dbKey) || '{}');
      return (db.financeiro || []).filter(f => f && f.userId === uid && !isDeleted(f.id));
    } catch {
      return [];
    }
  }, []);

  // ======================================================
  // 🔥 ENTERPRISE: getCompany COM CACHE
  // ======================================================
  const getCompanyWithCache = useCallback(async (userData) => {
    const userCompanyId = userData.companyId || userData.company || `company_${userData.uid || userData.id}`;
    
    if (companyCache.has(userCompanyId)) {
      return companyCache.get(userCompanyId);
    }
    
    if (companyFetchInProgress.has(userCompanyId)) {
      return companyFetchInProgress.get(userCompanyId);
    }
    
    const fetchCompany = async () => {
      try {
        const dbKey = `imoveispro_db_user_${userData.uid || userData.id}`;
        const db = JSON.parse(localStorage.getItem(dbKey) || "{}");
        const localCompany = db.company || {};
        
        const savedCompany = localStorage.getItem("company_data");
        let legacyCompany = {};
        if (savedCompany) {
          try {
            const parsed = JSON.parse(savedCompany);
            if (parsed.companyId && parsed.companyId !== userCompanyId) {
              localStorage.removeItem("company_data");
            } else {
              legacyCompany = parsed;
            }
          } catch (e) {
            localStorage.removeItem("company_data");
          }
        }
        
        let fireCompany = {};
        try {
          const { app } = await import("../lib/firebase/config.js");
          const { getFirestore, doc, getDoc } = await import("firebase/firestore");
          const fsDb = getFirestore(app);
          const companyRef = doc(fsDb, "public_companies", userCompanyId);
          const fireSnap = await getDoc(companyRef);
          if (fireSnap.exists()) {
            fireCompany = fireSnap.data();
          }
        } catch (e) {
          if (e.code !== 'permission-denied') {
            console.warn("⚠️ Não foi possível ler white label do Firestore:", e.message);
          }
        }
        
        const result = {
          nome: fireCompany.nome || localCompany.nome || legacyCompany.nome || userData.name || "Imobiliária",
          cor: fireCompany.cor || localCompany.cor || legacyCompany.cor || "#2563eb",
          logo: fireCompany.logo || localCompany.logo || legacyCompany.logo || null,
          slogan: fireCompany.slogan || legacyCompany.slogan || "",
          telefone: fireCompany.telefone || legacyCompany.telefone || "",
          telefoneWhatsApp: fireCompany.telefoneWhatsApp || legacyCompany.telefoneWhatsApp || "",
          email: fireCompany.email || legacyCompany.email || "",
          website: fireCompany.website || legacyCompany.website || "",
          mostrarMarcaDagua: fireCompany.mostrarMarcaDagua !== undefined ? fireCompany.mostrarMarcaDagua : (legacyCompany.mostrarMarcaDagua !== false),
          companyId: userCompanyId,
          updatedBy: userData.uid || userData.id
        };
        
        companyCache.set(userCompanyId, result);
        return result;
        
      } catch (error) {
        return {
          nome: userData.name || "Imobiliária",
          cor: "#2563eb",
          logo: null,
          companyId: userCompanyId
        };
      }
    };
    
    const promise = fetchCompany();
    companyFetchInProgress.set(userCompanyId, promise);
    
    const result = await promise;
    companyFetchInProgress.delete(userCompanyId);
    
    return result;
  }, []);

  // ======================================================
  // 🔄 INICIALIZAÇÃO DO DATABASE (V14 - ANTI-ZOMBIE)
  // ======================================================
  const initializeUserDatabase = useCallback(async (userData) => {
    try {
      userDataRef.current = userData;

      const existingInstance = DataContext.getInstance(userData.uid || userData.id);
      
      if (existingInstance) {
        dataContextRef.current = existingInstance;
      } else {
        dataContextRef.current = new DataContext(userData.uid || userData.id);
      }
      
      const dataContext = dataContextRef.current;
      
      if (dataContext) {
        let _cachedFinanceiro = lerFinanceiroLocal();
        
        const dbWrapper = {
          getProperties: () => dataContext.getProperties(),
          getPropertyById: (id) => dataContext.collections.properties.findById(id),
          createProperty: (data) => dataContext.createProperty(data),
          updateProperty: (id, data) => dataContext.updateProperty(id, data),
          getPropertyPreviewImage: async (propertyId) => {
            const prop = await dataContext.collections.properties.findById(propertyId);
            return prop?.images?.[0] || null;
          },
          deleteProperty: (id) => {
            addToDeletedCache(id);
            return dataContext.deleteProperty(id);
          },
          getPropertiesWithPreviews: async () => await dataContext.getProperties(),
          
          getClients: () => dataContext.getClients(),
          getClientById: (id) => dataContext.collections.clients.findById(id),
          createClient: (data) => dataContext.createClient(data),
          updateClient: (id, data) => dataContext.updateClient(id, data),
          deleteClient: (id) => {
            addToDeletedCache(id);
            return dataContext.deleteClient(id);
          },
          saveClient: (data, id) => dataContext.saveClient(data, id),
          
          getTasks: () => dataContext.collections.tasks.read(),
          getTaskById: (id) => dataContext.collections.tasks.findById(id),
          createTask: (data) => dataContext.collections.tasks.create(data),
          updateTask: (id, data) => dataContext.collections.tasks.update(id, data),
          deleteTask: (id) => {
            addToDeletedCache(id);
            return dataContext.collections.tasks.delete(id);
          },
          getTasksByClient: (clientId) => dataContext.getTasksByClient(clientId),
          
          getCalendarEvents: () => dataContext.collections.calendarEvents?.read() || [],
          createCalendarEvent: (data) => dataContext.collections.calendarEvents?.create(data),
          updateCalendarEvent: (id, data) => dataContext.collections.calendarEvents?.update(id, data),
          getCalendarEventsByProperty: (propertyId) => dataContext.strategy?.getCalendarEventsByProperty?.(propertyId, userData.uid) || [],
          
          getFinanceiro: () => _cachedFinanceiro,
          
          createFinanceiro: (data) => {
            const result = dataContext.collections.financeiro?.create(data);
            if (result) {
              result.then((saved) => {
                if (saved) {
                  _cachedFinanceiro = [saved, ..._cachedFinanceiro];
                }
              }).catch((error) => {
                console.error("❌ Erro ao criar financeiro via CloudStrategy:", error);
              });
            }
            return result;
          },
          
          deleteFinanceiro: (id) => {
            addToDeletedCache(id);
            const result = dataContext.collections.financeiro?.delete(id);
            _cachedFinanceiro = _cachedFinanceiro.filter(
              item => item.id !== id && item.uid !== id
            );
            return result;
          },
          
          getFinanceiroByProperty: async (propertyId) => {
            if (_cachedFinanceiro.length > 0) {
              return _cachedFinanceiro.filter(item => item.propertyId === propertyId);
            }
            const items = await dataContext.collections.financeiro?.read() || [];
            return items.filter(item => item.propertyId === propertyId);
          },
          
          registerSale: (propertyId, clientId) => {
            const prop = dataContext.getPropertyById(propertyId);
            if (!prop) return { success: false, error: 'Propiedad no encontrada' };
            
            const saleRecord = {
              id: `sale_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              type: "receita",
              category: "Venda de Imóvel",
              value: Number(prop.price) || 0,
              date: new Date().toISOString().split('T')[0],
              title: `Venda: ${prop.title || 'Imóvel'}`,
              description: `Venda do imóvel ${prop.title || ''}${clientId ? ` para cliente ${clientId}` : ''}`,
              propertyId: propertyId,
              clientId: clientId || prop.clientId || null,
              userId: userData.uid || userData.id,
              companyId: userData.companyId || userData.company || `company_${userData.uid || userData.id}`,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };
            
            dataContext.collections.financeiro?.create(saleRecord).then((saved) => {
              _cachedFinanceiro = [saved, ..._cachedFinanceiro];
            }).catch(() => {});
            
            return { success: true, record: saleRecord };
          },

          onFinanceiroSnapshot: (callback, onError) => {
            try {
              const { db, auth } = require("../lib/firebase/config");
              const currentUser = auth?.currentUser;
              
              if (!db || !currentUser) {
                const localData = lerFinanceiroLocal();
                callback(localData);
                return () => {};
              }
              
              currentUser.getIdTokenResult().then(tokenResult => {
                const cId = tokenResult.claims.companyId || tokenResult.claims.company;
                
                if (!cId) {
                  const localData = lerFinanceiroLocal();
                  callback(localData);
                  return () => {};
                }
                
                const { collection, query, where, orderBy, onSnapshot } = require("firebase/firestore");
                const ref = collection(db, "companies", cId, "financeiro");
                const q = query(ref, where("userId", "==", currentUser.uid), orderBy("createdAt", "desc"));
                
                const unsubscribe = onSnapshot(q,
                  (snap) => {
                    _cachedFinanceiro = snap.docs
                      .map(d => ({ id: d.id, ...d.data() }))
                      .filter(item => !isDeleted(item.id));
                    callback(_cachedFinanceiro);
                  },
                  (err) => {
                    console.warn("⚠️ Firestore snapshot error, usando localStorage:", err?.message);
                    const localData = lerFinanceiroLocal();
                    callback(localData);
                    if (onError) onError(err);
                  }
                );
                
                return unsubscribe;
              }).catch((err) => {
                console.warn("⚠️ Erro ao obter token claims:", err?.message);
                const localData = lerFinanceiroLocal();
                callback(localData);
                return () => {};
              });
            } catch (e) {
              console.warn("⚠️ Erro ao configurar snapshot:", e?.message);
              const localData = lerFinanceiroLocal();
              callback(localData);
              return () => {};
            }
          },
          
          registerRental: (propertyId, clientId, rentalData) => {
            return { success: true };
          },
          
          getDocuments: () => dataContext.collections.documents?.read() || [],
          getDocumentById: (id) => dataContext.collections.documents?.findById(id),
          createDocument: (data) => dataContext.collections.documents?.create(data),
          deleteDocument: (id) => {
            addToDeletedCache(id);
            return dataContext.collections.documents?.delete(id);
          },
          
          getMessages: async () => await dataContext.collections.messages?.read() || [],
          createMessage: (data) => dataContext.collections.messages?.create(data),
          getMessagesByClient: async (clientId) => {
            const items = await dataContext.collections.messages?.read() || [];
            return items.filter(item => item.clientId === clientId);
          },
          
          getCompany: async () => {
            return await getCompanyWithCache(userData);
          },
          
          saveCompany: async (companyData) => {
            if (!companyData) return null;
            const userCompanyId = userData.companyId || userData.company || `company_${userData.uid || userData.id}`;
            companyData.companyId = userCompanyId;
            
            const dbKey = `imoveispro_db_user_${userData.uid || userData.id}`;
            const db = JSON.parse(localStorage.getItem(dbKey) || "{}");
            db.company = companyData;
            localStorage.setItem(dbKey, JSON.stringify(db));
            localStorage.setItem('company_data', JSON.stringify(companyData));
            
            companyCache.set(userCompanyId, companyData);
            setCompany(companyData);
            
            try {
              const { app } = await import("../lib/firebase/config.js");
              const { getFirestore, doc, setDoc } = await import("firebase/firestore");
              const db = getFirestore(app);
              const companyRef = doc(db, "public_companies", userCompanyId);
              await setDoc(companyRef, {
                nome: companyData.nome || "",
                cor: companyData.cor || "#2563eb",
                logo: companyData.logo || "",
                slogan: companyData.slogan || "",
                telefone: companyData.telefone || "",
                telefoneWhatsApp: companyData.telefoneWhatsApp || "",
                email: companyData.email || "",
                website: companyData.website || "",
                mostrarMarcaDagua: companyData.mostrarMarcaDagua !== false,
                updatedAt: new Date().toISOString()
              }, { merge: true });
            } catch (e) {}
            
            window.dispatchEvent(new CustomEvent('companyUpdated', { detail: companyData }));
            return companyData;
          },
          
          getDashboardStats: () => dataContext.getDashboardStats(),
          getMonthlySales: (months = 6) => {
            const result = [];
            const today = new Date();
            for (let i = months - 1; i >= 0; i--) {
              const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
              const monthStr = date.toLocaleDateString("pt-BR", { month: "short", year: "numeric" });
              const monthSales = _cachedFinanceiro.filter(f => {
                if (f.type !== "receita" || f.category !== "Venda de Imóvel") return false;
                const fDate = new Date(f.date || f.createdAt);
                return fDate.getMonth() === date.getMonth() && fDate.getFullYear() === date.getFullYear();
              });
              result.push({ month: monthStr, value: monthSales.reduce((s, f) => s + (Number(f.value) || 0), 0), fullDate: date });
            }
            return result;
          },
          getSalesGrowth: () => {
            const monthly = [];
            const today = new Date();
            for (let i = 1; i >= 0; i--) {
              const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
              const monthSales = _cachedFinanceiro.filter(f => {
                if (f.type !== 'receita' || f.category !== 'Venda de Imóvel') return false;
                const fDate = new Date(f.date || f.createdAt);
                return fDate.getMonth() === date.getMonth() && fDate.getFullYear() === date.getFullYear();
              });
              monthly.push({ value: monthSales.reduce((s, f) => s + (Number(f.value) || 0), 0) });
            }
            if (monthly.length < 2) return { percentage: 0, trend: 'stable', formatted: '0%' };
            const previous = monthly[0]?.value || 0;
            const current = monthly[1]?.value || 0;
            if (previous === 0) return { percentage: current > 0 ? 100 : 0, trend: current > 0 ? 'up' : 'stable', formatted: current > 0 ? '+100%' : '0%' };
            const growth = ((current - previous) / previous) * 100;
            const rounded = Math.round(growth * 10) / 10;
            return { percentage: rounded, trend: rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'stable', formatted: `${rounded > 0 ? '+' : ''}${rounded}%` };
          },
          saveUserPreferences: (data) => {
            const key = `user_preferences_${userData.uid || userData.id}`;
            const existing = JSON.parse(localStorage.getItem(key) || '{}');
            const updated = { ...existing, ...data, updatedAt: new Date().toISOString() };
            localStorage.setItem(key, JSON.stringify(updated));
            return updated;
          },
          
          syncNow: () => dataContext.syncNow?.(),
          getSyncStatus: () => dataContext.getSyncStatus?.(),
          syncManager: dataContext.syncManager,
          healthCheck: () => dataContext.healthCheck?.(),
          deduplicateAllCollections: () => dataContext.deduplicateAllCollections?.() || true,
          
          addNoteToClient: async (clientId, note) => {
            const client = await dataContext.collections.clients.findById(clientId);
            if (!client) return null;
            const newNote = {
              id: Date.now().toString(),
              text: note,
              createdAt: new Date().toISOString(),
              createdBy: userData.uid || userData.id
            };
            const notes = [...(client.notes || []), newNote];
            await dataContext.updateClient(clientId, { notes, updatedAt: new Date().toISOString() });
            return newNote;
          },
          deleteNoteFromClient: async (clientId, noteId) => {
            const client = await dataContext.collections.clients.findById(clientId);
            if (!client) return false;
            const notes = (client.notes || []).filter(n => n.id !== noteId);
            await dataContext.updateClient(clientId, { notes, updatedAt: new Date().toISOString() });
            return true;
          },
          editNoteFromClient: async (clientId, noteId, newText) => {
            const client = await dataContext.collections.clients.findById(clientId);
            if (!client) return false;
            const notes = (client.notes || []).map(n => 
              n.id === noteId ? { ...n, text: newText, editedAt: new Date().toISOString() } : n
            );
            await dataContext.updateClient(clientId, { notes, updatedAt: new Date().toISOString() });
            return true;
          },
          linkPropertyToClient: async (propertyId, clientId) => {
            const prop = dataContext.getPropertyById(propertyId);
            if (!prop) return false;
            const linkedClients = prop.linkedClients || [];
            if (linkedClients.includes(clientId)) return true;
            return dataContext.updateProperty(propertyId, {
              linkedClients: [...linkedClients, clientId],
              clientId: clientId,
              updatedAt: new Date().toISOString()
            }).then(() => true).catch(() => false);
          },
          unlinkProperty: async (propertyId) => {
            const prop = dataContext.getPropertyById(propertyId);
            if (!prop) return false;
            return dataContext.updateProperty(propertyId, {
              linkedClients: [],
              clientId: null,
              updatedAt: new Date().toISOString()
            }).then(() => true).catch(() => false);
          },
          updatePropertyStatus: (propertyId, status, clientId) => true
        };
        
        setDatabase(dbWrapper);
        
        const companyData = await getCompanyWithCache(userData);
        if (mountedRef.current) {
          setCompany(companyData);
        }
        
        return dbWrapper;
      }
      return null;
    } catch (error) {
      console.error("❌ Erro inicializando database:", error);
      return null;
    }
  }, [getCompanyWithCache, lerFinanceiroLocal]);

  // ======================================================
  // 🔄 CARGA INICIAL DE SESSÃO - V17 MULTI-DEVICE + TRIAL CHECK
  // ======================================================
  useEffect(() => {
    mountedRef.current = true;
    
    const initAuth = async () => {
      if (initializedRef.current) return;
      
      try {
        const mode = authService.getMode();
        setAuthMode(mode);
        
        let savedUser = localStorage.getItem('auth_user');
        const savedToken = localStorage.getItem('auth_token');
        
        if (!savedUser) {
          try {
            const { auth, firebaseAvailable } = await import('../lib/firebase/config');
            if (firebaseAvailable && auth?.currentUser) {
              console.log('🔄 [V17] localStorage vacío, cargando desde Firestore...');
              const firestoreUserData = await getUserFromFirestore(auth.currentUser.uid);
              if (firestoreUserData) {
                savedUser = JSON.stringify(firestoreUserData);
                console.log('✅ [V17] Datos recuperados de Firestore y guardados en localStorage');
              }
            }
          } catch (e) {
            console.warn('⚠️ [V17] No se pudo cargar desde Firestore:', e.message);
          }
        }
        
        if (savedUser && savedToken) {
          try {
            const parsedUser = JSON.parse(savedUser);
            try {
              const tokenData = JSON.parse(atob(savedToken));
              if (tokenData.exp && tokenData.exp < Date.now()) throw new Error('Token expirado');
            } catch (tokenError) {
              localStorage.removeItem('auth_token');
              localStorage.removeItem('auth_user');
              if (mountedRef.current) setLoadingAuth(false);
              return;
            }
            
            const completeUser = createCompleteUserObject(parsedUser);
            if (completeUser && mountedRef.current) {
              setUser(completeUser);
              await initializeUserDatabase(completeUser);
              
              const trialExpired = checkTrialAndRedirect();
              if (trialExpired) return;
              
              emitNotification('success', `Bem-vindo de volta, ${completeUser.name}!`, { duration: 3000 });
            }
          } catch (parseError) {
            localStorage.removeItem('auth_user');
            localStorage.removeItem('auth_token');
          }
        }
      } catch (error) {
        console.error("❌ Erro na carga inicial:", error);
      } finally {
        if (mountedRef.current) {
          initializedRef.current = true;
          setLoadingAuth(false);
        }
      }
    };
    
    initAuth();
    
    const unsubscribe = authService.onAuthChange(async (authUser) => {
      if (!mountedRef.current) return;
      
      if (authUser) {
        const completeUser = createCompleteUserObject(authUser);
        setUser(completeUser);
        await initializeUserDatabase(completeUser);
      } else {
        setUser(null);
        setDatabase(null);
        setCompany(null);
      }
    });
    
    return () => {
      mountedRef.current = false;
      unsubscribe?.();
    };
  }, [createCompleteUserObject, initializeUserDatabase, emitNotification, getUserFromFirestore, checkTrialAndRedirect]);

  // ======================================================
  // 🔐 V17: LOGIN BLINDADO
  // ======================================================
  const login = useCallback(async (email, password) => {
    // Bloquear doble submit
    if (isSubmittingRef.current) {
      emitNotification('error', 'Login já em andamento. Aguarde.');
      return false;
    }
    
    // Verificar conexión
    if (!isOnline()) {
      emitNotification('error', 'Sem conexão com a internet.');
      return false;
    }
    
    isSubmittingRef.current = true;
    
    try {
      // Rate limit
      checkRateLimit();
      
      emitNotification('info', 'Autenticando...', { duration: 2000 });
      
      // Timeout + operación
      const authenticatedUser = await executeWithTimeout(
        () => authService.login(email, password),
        'login'
      );
      
      if (!authenticatedUser) {
        emitNotification('error', 'Email ou senha incorretos');
        return false;
      }
      
      const loggedUser = createCompleteUserObject(authenticatedUser);
      if (!loggedUser) throw new Error("Não foi possível criar o objeto user completo");
      
      setUser(loggedUser);
      
      const tokenData = {
        userId: loggedUser.id,
        email: loggedUser.email,
        role: loggedUser.role,
        exp: Date.now() + 7 * 24 * 60 * 60 * 1000
      };
      localStorage.setItem("auth_token", btoa(JSON.stringify(tokenData)));
      localStorage.setItem("auth_user", JSON.stringify(loggedUser));
      localStorage.setItem("auth_provider", loggedUser._authProvider);
      
      await initializeUserDatabase(loggedUser);
      
      checkTrialAndRedirect();
      
      emitNotification('success', `Bem-vindo, ${loggedUser.name}!`, { title: 'Login realizado' });
      emitAddNotification({
        type: 'success',
        title: 'Login realizado',
        message: `Bem-vindo de volta, ${loggedUser.name}!`,
        metadata: { userId: loggedUser.id, loginTime: new Date().toISOString(), provider: loggedUser._authProvider }
      });
      
      return true;
      
    } catch (err) {
      console.error("❌ Erro no login:", err);
      if (err.message.includes('Muitas tentativas')) {
        emitNotification('error', 'Muitas tentativas. Aguarde 30 segundos.');
      } else {
        emitNotification('error', err.message || 'Erro ao fazer login');
      }
      return false;
    } finally {
      isSubmittingRef.current = false;
    }
  }, [createCompleteUserObject, initializeUserDatabase, emitNotification, emitAddNotification, checkTrialAndRedirect]);

  // ======================================================
  // 🔥 V17: LOGIN COM GOOGLE BLINDADO
  // ======================================================
  const loginWithGoogle = useCallback(async (googleUser) => {
    if (isSubmittingRef.current) {
      emitNotification('error', 'Login já em andamento. Aguarde.');
      return false;
    }
    
    if (!isOnline()) {
      emitNotification('error', 'Sem conexão com a internet.');
      return false;
    }
    
    isSubmittingRef.current = true;
    
    try {
      checkRateLimit();
      
      emitNotification('info', 'Conectando com Google...', { duration: 2000 });
      
      if (!authService.loginWithGoogle) {
        throw new Error('Método loginWithGoogle não disponível em authService');
      }
      
      if (googleUser && typeof googleUser.getIdToken === 'function') {
        await googleUser.getIdToken(true);
        const token = await googleUser.getIdTokenResult();
        window._debugGoogleUser = googleUser;
        window._debugAuthService = authService;
      }
      
      const processedUser = await executeWithTimeout(
        () => authService.loginWithGoogle(googleUser),
        'loginWithGoogle'
      );
      
      if (!processedUser) throw new Error('Não foi possível processar o usuário do Google');
      
      const loggedUser = createCompleteUserObject({ ...processedUser, _authProvider: 'google' });
      if (!loggedUser) throw new Error('Não foi possível criar o objeto user completo');
      
      setUser(loggedUser);
      setAuthMode('production');
      
      const tokenData = {
        userId: loggedUser.id,
        email: loggedUser.email,
        role: loggedUser.role,
        exp: Date.now() + 7 * 24 * 60 * 60 * 1000
      };
      localStorage.setItem("auth_token", btoa(JSON.stringify(tokenData)));
      localStorage.setItem("auth_user", JSON.stringify(loggedUser));
      localStorage.setItem("auth_provider", 'google');
      
      await initializeUserDatabase(loggedUser);
      
      checkTrialAndRedirect();
      
      emitNotification('success', `Bem-vindo, ${loggedUser.name}!`, { title: 'Login com Google realizado' });
      emitAddNotification({
        type: 'success',
        title: 'Login com Google',
        message: `Bem-vindo, ${loggedUser.name}!`,
        metadata: { userId: loggedUser.id, loginTime: new Date().toISOString(), provider: 'google' }
      });
      
      return true;
      
    } catch (err) {
      console.error("❌ Erro no loginWithGoogle:", err);
      if (err.message.includes('Muitas tentativas')) {
        emitNotification('error', 'Muitas tentativas. Aguarde 30 segundos.');
      } else {
        emitNotification('error', err.message || 'Erro ao conectar com Google');
      }
      return false;
    } finally {
      isSubmittingRef.current = false;
    }
  }, [createCompleteUserObject, initializeUserDatabase, emitNotification, emitAddNotification, checkTrialAndRedirect]);

  // ======================================================
  // 📝 V17: CADASTRO BLINDADO
  // ======================================================
  const signUp = useCallback(async (userData) => {
    if (isSubmittingRef.current) {
      emitNotification('error', 'Cadastro já em andamento. Aguarde.');
      return false;
    }
    
    if (!isOnline()) {
      emitNotification('error', 'Sem conexão com a internet.');
      return false;
    }
    
    if (!userData.email || !userData.password || !userData.name) {
      emitNotification('error', 'Email, senha e nome são obrigatórios');
      return false;
    }
    
    isSubmittingRef.current = true;
    
    try {
      checkRateLimit();
      
      if (authService.getMode() === 'production') {
        try {
          emitNotification('info', 'Criando conta na nuvem...', { duration: 3000 });
          await executeWithTimeout(
            () => authService.signUp(userData.email, userData.password, userData),
            'signUp'
          );
          emitNotification('success', 'Conta criada! Redirecionando...', { duration: 2000 });
          return await login(userData.email, userData.password);
        } catch (error) {
          if (error.message.includes('email-already-in-use')) {
            emitNotification('error', 'Este email já está registrado');
          } else if (error.message.includes('weak-password')) {
            emitNotification('error', 'A senha é muito fraca. Use pelo menos 6 caracteres');
          } else {
            emitNotification('error', error.message || 'Erro ao criar conta');
          }
          return false;
        }
      }
      
      const usersDb = getUsersDatabase();
      const existingUser = usersDb.users.find(u => u.email === userData.email);
      if (existingUser) {
        emitNotification('error', 'Este email já está registrado');
        return false;
      }
      
      const hashedPassword = await bcrypt.hash(userData.password, 10);
      const newUser = {
        id: `user_${Date.now()}`,
        email: userData.email,
        password: hashedPassword,
        name: userData.name,
        role: userData.role || 'usuario',
        company: userData.company || '',
        avatar: null,
        phone: userData.phone || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        emailVerified: false,
        passwordVersion: 'bcrypt',
        registeredVia: 'signup'
      };
      
      usersDb.users.push(newUser);
      saveUsersDatabase(usersDb);
      
      emitNotification('success', 'Conta DEMO criada!');
      return await login(userData.email, userData.password);
      
    } catch (err) {
      console.error("❌ Erro no cadastro:", err);
      if (err.message.includes('Muitas tentativas')) {
        emitNotification('error', 'Muitas tentativas. Aguarde 30 segundos.');
      } else {
        emitNotification('error', 'Erro ao realizar cadastro');
      }
      return false;
    } finally {
      isSubmittingRef.current = false;
    }
  }, [login, emitNotification]);

  // ======================================================
  // 🔑 ESQUECI MINHA SENHA
  // ======================================================
  const forgotPassword = useCallback(async (email) => {
    try {
      const result = await authService.forgotPassword(email);
      if (result.success) {
        emitNotification('info', 'Se o email existir, enviaremos instruções');
      } else {
        emitNotification('error', result.message || 'Erro ao solicitar recuperação');
      }
      return result;
    } catch (err) {
      console.error("❌ Erro no forgotPassword:", err);
      emitNotification('error', 'Erro ao solicitar recuperação');
      return { success: false };
    }
  }, [emitNotification]);

  // ======================================================
  // 🔄 REDEFINIR SENHA
  // ======================================================
  const resetPassword = useCallback(async (token, newPassword) => {
    try {
      const resetRequests = JSON.parse(localStorage.getItem('password_reset_requests') || '[]');
      const request = resetRequests.find(r => r.token === token && !r.used);
      
      if (!request || request.expiresAt < Date.now()) {
        emitNotification('error', 'Token inválido ou expirado');
        return false;
      }
      
      const usersDb = getUsersDatabase();
      const userIndex = usersDb.users.findIndex(u => u.id === request.userId);
      
      if (userIndex === -1) {
        emitNotification('error', 'Usuário não encontrado');
        return false;
      }
      
      const hashedPassword = await bcrypt.hash(newPassword, 10);
      usersDb.users[userIndex].password = hashedPassword;
      usersDb.users[userIndex].updatedAt = new Date().toISOString();
      usersDb.users[userIndex].passwordVersion = 'bcrypt';
      request.used = true;
      
      localStorage.setItem('password_reset_requests', JSON.stringify(resetRequests));
      saveUsersDatabase(usersDb);
      
      emitNotification('success', 'Senha alterada com sucesso!');
      return true;
    } catch (err) {
      console.error("❌ Erro no resetPassword:", err);
      emitNotification('error', 'Erro ao alterar senha');
      return false;
    }
  }, [emitNotification]);

  // ======================================================
  // ✅ VERIFICAR EMAIL
  // ======================================================
  const verifyEmail = useCallback(async (token) => {
    try {
      const verifyTokens = JSON.parse(localStorage.getItem('email_verify_tokens') || '[]');
      const verifyRequest = verifyTokens.find(t => t.token === token && !t.used);
      
      if (!verifyRequest || verifyRequest.expiresAt < Date.now()) {
        emitNotification('error', 'Token de verificação inválido ou expirado');
        return false;
      }
      
      const usersDb = getUsersDatabase();
      const userIndex = usersDb.users.findIndex(u => u.id === verifyRequest.userId);
      
      if (userIndex !== -1) {
        usersDb.users[userIndex].emailVerified = true;
        usersDb.users[userIndex].updatedAt = new Date().toISOString();
        saveUsersDatabase(usersDb);
      }
      
      verifyRequest.used = true;
      localStorage.setItem('email_verify_tokens', JSON.stringify(verifyTokens));
      
      emitNotification('success', 'Email verificado com sucesso!');
      return true;
    } catch (err) {
      console.error("❌ Erro no verifyEmail:", err);
      emitNotification('error', 'Erro ao verificar email');
      return false;
    }
  }, [emitNotification]);

  // ======================================================
  // 🎉 COMPLETAR ONBOARDING
  // ======================================================
  const completeOnboarding = useCallback(async (preferences) => {
    try {
      if (!user) {
        emitNotification('error', 'Usuário não autenticado');
        return false;
      }
      
      if (database) {
        database.saveUserPreferences({
          onboardingCompleted: true,
          onboardingCompletedAt: new Date().toISOString(),
          ...preferences
        });
      }
      
      const usersDb = getUsersDatabase();
      const userIndex = usersDb.users.findIndex(u => u.id === user.id);
      
      if (userIndex !== -1) {
        usersDb.users[userIndex].onboardingCompleted = true;
        usersDb.users[userIndex].onboardingCompletedAt = new Date().toISOString();
        usersDb.users[userIndex].preferences = preferences;
        saveUsersDatabase(usersDb);
      }
      
      setUser(prev => prev ? { ...prev, onboardingCompleted: true, preferences } : prev);
      emitNotification('success', 'Configuração concluída!');
      return true;
    } catch (err) {
      console.error("❌ Erro no completeOnboarding:", err);
      emitNotification('error', 'Erro ao finalizar configuração');
      return false;
    }
  }, [user, database, emitNotification]);

  // ======================================================
  // 🔄 QUICK LOGIN
  // ======================================================
  const quickLogin = useCallback(async (userId) => {
    try {
      const usersDb = getUsersDatabase();
      const userToLogin = usersDb.users.find(u => u.id === userId);
      if (!userToLogin) {
        emitNotification('error', 'Usuário não encontrado');
        return false;
      }
      return await login(userToLogin.email, '123456');
    } catch (err) {
      console.error("❌ Erro no quickLogin:", err);
      emitNotification('error', 'Erro no login rápido');
      return false;
    }
  }, [login, emitNotification]);

  // ======================================================
  // 👤 ATUALIZAR USUÁRIO
  // ======================================================
  const updateUser = useCallback(async (partialUser) => {
    try {
      if (!partialUser || typeof partialUser !== "object") {
        throw new Error("Dados inválidos para atualização do usuário");
      }
      if (!user) throw new Error("Usuário não encontrado no estado");
      
      const updatedUserData = { ...user, ...partialUser, updatedAt: new Date().toISOString() };
      const updatedUser = createCompleteUserObject(updatedUserData);
      if (!updatedUser) throw new Error("Não foi possível criar o objeto user completo");
      
      if (authService.getMode() === 'production') {
        try {
          await authService.updateUserProfile(user.id, partialUser);
          
          if (partialUser.avatar) {
            const { getAuth, updateProfile } = await import('firebase/auth');
            const auth = getAuth();
            if (auth.currentUser) {
              await updateProfile(auth.currentUser, { photoURL: partialUser.avatar });
            }
          }
        } catch (firestoreError) {
          console.warn('⚠️ Erro ao atualizar Firestore/Auth:', firestoreError.message);
        }
      }
      
      localStorage.setItem("auth_user", JSON.stringify(updatedUser));
      
      const usersDb = getUsersDatabase();
      const userIndex = usersDb.users.findIndex(u => u.id === user.id);
      if (userIndex !== -1) {
        usersDb.users[userIndex] = { ...usersDb.users[userIndex], ...partialUser, updatedAt: new Date().toISOString() };
        saveUsersDatabase(usersDb);
      }
      
      setUser(updatedUser);
      
      if (partialUser.company && partialUser.company !== user.company) {
        await initializeUserDatabase(updatedUser);
      }
      
      emitNotification('success', 'Perfil atualizado com sucesso!');
      return true;
    } catch (err) {
      console.error("❌ Erro ao atualizar usuário:", err);
      emitNotification('error', 'Erro ao atualizar perfil');
      throw err;
    }
  }, [user, createCompleteUserObject, initializeUserDatabase, emitNotification]);

  // ======================================================
  // 🏢 ATUALIZAR EMPRESA
  // ======================================================
  const updateCompany = useCallback(async (newCompany) => {
    try {
      if (!database || !newCompany) throw new Error("Base de dados ou empresa inválida");
      await database.saveCompany?.(newCompany);
      emitNotification('success', 'Empresa atualizada com sucesso!');
      return true;
    } catch (err) {
      console.error("❌ Erro ao atualizar empresa:", err);
      emitNotification('error', 'Erro ao atualizar empresa');
      return false;
    }
  }, [database, emitNotification]);

  // ======================================================
  // 🔓 LOGOUT
  // ======================================================
  const logout = useCallback(async () => {
    const userName = user?.name;
    try {
      await authService.logout();
    } catch (error) {
      console.warn('⚠️ Erro no logout do authService:', error);
    }
    setUser(null);
    setDatabase(null);
    setCompany(null);
    emitNotification('info', `Até logo, ${userName || 'usuário'}!`, { duration: 3000 });
    return true;
  }, [user, emitNotification]);

  // ======================================================
  // 🛠️ FERRAMENTAS DE DEBUG
  // ======================================================
  const debugAuthState = useCallback(() => {
    console.log("=== DEBUG AUTH STATE ===", {
      user,
      database: !!database,
      company,
      loadingAuth,
      authMode,
      companyCacheSize: companyCache.size
    });
  }, [user, database, company, loadingAuth, authMode]);

  const getUserForCloudStrategy = useCallback(() => {
    if (!user) return null;
    return {
      uid: user.uid || user.id,
      id: user.id,
      companyId: user.companyId || user.company,
      email: user.email,
      role: user.role,
      canWrite: user.canWrite,
      canDelete: user.canDelete,
      canRead: user.canRead,
      name: user.name,
      _tenantId: user._tenantId,
      _securityLevel: user._securityLevel
    };
  }, [user]);

  // ======================================================
  // 📦 MODAL DE CONFIRMAÇÃO
  // ======================================================
  const openConfirm = useCallback((config) => setModalData(config), []);
  const closeConfirm = useCallback(() => setModalData(null), []);

  // ======================================================
  // 📤 VALOR DO CONTEXTO (MEMOIZADO)
  // ======================================================
  const contextValue = useMemo(() => ({
    user, database, company, loadingAuth, authMode,
    login, loginWithGoogle, logout, updateUser, updateCompany, quickLogin,
    signUp, forgotPassword, resetPassword, verifyEmail, completeOnboarding,
    setUser, setCompany,
    debugAuthState, getUserForCloudStrategy, openConfirm,
    showToast: emitNotification, addNotification: emitAddNotification
  }), [
    user, database, company, loadingAuth, authMode,
    login, loginWithGoogle, logout, updateUser, updateCompany, quickLogin,
    signUp, forgotPassword, resetPassword, verifyEmail, completeOnboarding,
    debugAuthState, getUserForCloudStrategy, openConfirm,
    emitNotification, emitAddNotification
  ]);

  // ======================================================
  // 🎨 RENDERIZAÇÃO
  // ======================================================
  return (
    <AuthContext.Provider value={contextValue}>
      <ConfirmModal data={modalData} onClose={closeConfirm} />
      {children}
    </AuthContext.Provider>
  );
}